"use client";

import {
    getReviewItemApiV1AdminReviewReviewTypeItemIdGet,
    ensureLessonGeneratedApiV1CoursesCourseIdLessonsLessonIdEnsureGeneratedPost,
    getReviewLessonQuizApiV1AdminReviewLessonItemUuidQuizGet,
    listReviewProactiveLessonsApiV1AdminReviewLessonItemUuidProactiveListGet,
    previewReviewProactiveLessonApiV1AdminReviewLessonItemUuidProactivePreviewGet,
    previewReviewRemedialLessonApiV1AdminReviewLessonItemUuidRemedialPreviewGet,
    updateReviewItemApiV1AdminReviewReviewTypeItemIdPatch,
    approveReviewItemApiV1AdminReviewReviewTypeItemIdApprovePost,
    queueReviewCanonicalGenerationApiV1AdminReviewLessonItemUuidGenerateCanonicalPost,
} from "@/src/api/sdk.gen";
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "@/src/lib/next-compat";
import {  useNavigate  } from "react-router-dom";
import { dynamic } from "@/src/lib/next-compat";

import {
    ChevronLeft,
    CheckCircle,
    XCircle,
    PenSquare,
    RefreshCw,
    FileQuestion,
    MoreHorizontal,
    Save,
    Sparkles,
} from "lucide-react";

import type { MenuItem } from "@/components/ui/MenuDropdown";
import QuizReviewContent from "@/components/admin/QuizReviewContent";
import RejectionModal from "@/components/admin/RejectionModal";
import RegenerateLessonDialog from "@/components/admin/RegenerateLessonDialog";
import GenerateProactiveDialog from "@/components/admin/GenerateProactiveDialog";
import GenerateRemedialDialog from "@/components/admin/GenerateRemedialDialog";
import ProactiveLessonContent, {
    isProactiveStructuredData,
} from "@/components/student/ProactiveLessonContent";
import ProfessionalProactiveLessonContent, {
    isProfessionalProactiveStructuredData,
} from "@/components/student/ProfessionalProactiveLessonContent";
import RemedialLessonContent, {
    isStructuredRemedialData,
    isProfessionalRemedialData,
    type RemedialLessonData,
    type ProfessionalRemedialLessonData,
} from "@/components/student/RemedialLessonContent";
import type { HistorySidebarMode, ProactiveListEntry } from "@/components/admin/VersionHistoryPanel";
import type {
    ProactivePreviewState,
    RemedialPreviewState,
    ReviewItem,
    ToastState,
} from "@/components/admin/review/types";
import { exportLessonRootToPdf, lessonPdfFilename } from "@/lib/lesson-pdf-export";

const MenuDropdown = dynamic(
    () => import("@/components/ui/MenuDropdown").then((m) => m.MenuDropdown),
    { ssr: false },
);

const LessonReviewContent = dynamic(
    () => import("@/components/admin/LessonReviewContent"),
    {
        ssr: false,
        loading: () => (
            <div className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="h-6 w-40 animate-pulse rounded bg-slate-100" />
                <div className="mt-4 h-48 w-full animate-pulse rounded bg-slate-100" />
            </div>
        ),
    },
);

export default function ReviewDetailClient({
    type,
    id,
    initialItem,
}: {
    type: string;
    id: string;
    initialItem: ReviewItem | null;
}) {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(!initialItem);
    const [error, setError] = useState("");

    const [isEditing, setIsEditing] = useState(false);
    const [draftItem, setDraftItem] = useState<ReviewItem | null>(initialItem);
    const [isSaving, setIsSaving] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

    const [toast, setToast] = useState<ToastState>(null);
    const [isViewingVersion, setIsViewingVersion] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
    const [isCanonicalRegenerating, setIsCanonicalRegenerating] = useState(false);
    const [isGenerateProactiveOpen, setIsGenerateProactiveOpen] = useState(false);
    const [isGenerateRemedialOpen, setIsGenerateRemedialOpen] = useState(false);
    const [hasLessonQuiz, setHasLessonQuiz] = useState<boolean>(false);
    const [sidebarMode, setSidebarMode] = useState<HistorySidebarMode>("canonical");
    const [proactiveList, setProactiveList] = useState<ProactiveListEntry[]>([]);
    const [proactiveListLoading, setProactiveListLoading] = useState(false);
    const [isSavingProactivePdf, setIsSavingProactivePdf] = useState(false);
    const proactivePdfContentRef = useRef<HTMLDivElement | null>(null);
    const [proactivePreview, setProactivePreview] = useState<ProactivePreviewState>(null);
    const [remedialPreview, setRemedialPreview] = useState<RemedialPreviewState>(null);

    const showToast = (message: string, type: "success" | "error" = "success") => {
        setToast({ message, type });
    };

    const fetchItem = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error } = await getReviewItemApiV1AdminReviewReviewTypeItemIdGet({
                path: { review_type: type, item_id: id },
            });
            if (error || !data) throw new Error("Failed to fetch review item");
            setDraftItem((data as any).item);
            setError("");
        } catch (err) {
            setError("Failed to load review item");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [type, id]);

    // Fetch review item if not supplied initially
    useEffect(() => {
        if (!initialItem && id && type) {
            fetchItem();
        } else if (initialItem) {
            setDraftItem(initialItem);
            setError("");
        }
    }, [type, id, initialItem, fetchItem]);

    // Always scroll to top when opening a review item or switching variants
    useEffect(() => {
        if (typeof window === "undefined") return;

        // Admin layout uses a scrollable <main> container; prefer scrolling that.
        const main = document.querySelector("main.flex-1.overflow-y-auto") as HTMLElement | null;
        if (main) {
            main.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } else {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
    }, [type, id]);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Poll canonical generation status while regeneration is in progress
    useEffect(() => {
        if (!isCanonicalRegenerating || type !== "lesson" || !draftItem) return;

        let cancelled = false;

        const courseId = (draftItem as any)?.lessons?.modules?.courses?.id;
        const canonicalLessonId = (draftItem as any)?.lesson_id;

        if (!courseId || !canonicalLessonId) {
            setIsCanonicalRegenerating(false);
            return;
        }

        const poll = async () => {
            if (cancelled) return;
            try {
                const { data, error } = await ensureLessonGeneratedApiV1CoursesCourseIdLessonsLessonIdEnsureGeneratedPost({
                    path: { course_id: courseId, lesson_id: canonicalLessonId },
                });
                if (error || !data) return;
                const resData = data as any;
                if (resData.status) {
                    setIsCanonicalRegenerating(false);
                    await fetchItem();
                } else if (!cancelled) {
                    setTimeout(poll, 4000);
                }
            } catch {
                if (!cancelled) {
                    setTimeout(poll, 6000);
                }
            }
        };

        poll();

        return () => {
            cancelled = true;
        };
    }, [isCanonicalRegenerating, type, draftItem]);

    useEffect(() => {
        setProactivePreview(null);
        setRemedialPreview(null);
        setSidebarMode("canonical");
        setProactiveList([]);
        setHasLessonQuiz(false);
    }, [id]);

    useEffect(() => {
        if (type !== "lesson") return;
        let cancelled = false;
        const load = async () => {
            try {
                const { data, error } = await getReviewLessonQuizApiV1AdminReviewLessonItemUuidQuizGet({
                    path: { item_uuid: id },
                });
                const resData = (data || {}) as any;
                if (!cancelled) {
                    setHasLessonQuiz(!error && Boolean(resData.quiz));
                }
            } catch {
                if (!cancelled) setHasLessonQuiz(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [id, type]);

    const fetchProactiveList = useCallback(async () => {
        if (type !== "lesson") return;
        setProactiveListLoading(true);
        try {
            const { data, error } = await listReviewProactiveLessonsApiV1AdminReviewLessonItemUuidProactiveListGet({
                path: { item_uuid: id },
            });
            const resData = (data || {}) as any;
            if (!error && Array.isArray(resData.entries)) {
                setProactiveList(resData.entries as ProactiveListEntry[]);
            } else {
                setProactiveList([]);
            }
        } catch {
            setProactiveList([]);
        } finally {
            setProactiveListLoading(false);
        }
    }, [type, id]);

    useEffect(() => {
        if (type !== "lesson" || sidebarMode !== "proactive") return;
        void fetchProactiveList();
    }, [type, id, sidebarMode, fetchProactiveList]);

    useEffect(() => {
        if (sidebarMode !== "proactive") return;
        if (proactivePreview) return;
        const firstWithStructured = proactiveList.find((e) => e.has_structured);
        if (firstWithStructured) {
            setProactivePreview({
                domainLabel: firstWithStructured.domain_label ?? "—",
                futureGoalLabel: firstWithStructured.future_goal_label ?? "—",
                structured: firstWithStructured.structured_content,
                status: firstWithStructured.status,
                polling: false,
                pollError: null,
                selectedRowId: firstWithStructured.id,
            });
        }
    }, [sidebarMode, proactiveList, proactivePreview]);

    useEffect(() => {
        if (!proactivePreview?.polling || type !== "lesson") return;
        const { domainLabel, futureGoalLabel } = proactivePreview;
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 45;
        const intervalMs = 3000;

        const tick = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const { data, error } = await previewReviewProactiveLessonApiV1AdminReviewLessonItemUuidProactivePreviewGet({
                    path: { item_uuid: id },
                    query: { domain_label: domainLabel, future_goal_label: futureGoalLabel },
                });
                const resData = (data || {}) as any;
                if (error) {
                    if (!cancelled) {
                        setProactivePreview((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      polling: false,
                                      pollError:
                                          typeof resData.error === "string"
                                              ? resData.error
                                              : "Failed to load proactive preview",
                                  }
                                : null,
                        );
                    }
                    return;
                }
                const structured = resData.structured_content ?? null;
                const serverReady = resData.ready === true;
                const contentValid = structured !== null && (isProactiveStructuredData(structured) || isProfessionalProactiveStructuredData(structured));
                const ready = serverReady && contentValid;
                // If the server already has a result but it's not valid structured content,
                // stop polling immediately — retrying won't change the stored content.
                const doneWithBadContent = serverReady && !contentValid;
                const status = typeof data.status === "string" ? data.status : null;

                if (!cancelled) {
                    setProactivePreview((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  structured,
                                  status,
                                  polling: !ready && !doneWithBadContent && attempts < maxAttempts,
                                  pollError: null,
                              }
                            : null,
                    );
                }

                if (ready || doneWithBadContent || attempts >= maxAttempts) {
                    if (!cancelled) {
                        void fetchProactiveList();
                    }
                    if (!ready && !cancelled) {
                        setProactivePreview((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      polling: false,
                                      pollError:
                                          status && /fail|error/i.test(status)
                                              ? `Generation status: ${status}`
                                              : doneWithBadContent
                                                ? "Generation saved invalid content (possibly from a previous failed attempt). Delete the saved version and regenerate."
                                                : "Timed out waiting for structured proactive content. Check workers or try again.",
                                  }
                                : null,
                        );
                    }
                    return;
                }
                setTimeout(tick, intervalMs);
            } catch {
                if (!cancelled) {
                    setProactivePreview((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  polling: attempts < maxAttempts,
                                  pollError:
                                      attempts >= maxAttempts ? "Preview request failed repeatedly." : null,
                              }
                            : null,
                    );
                    if (attempts < maxAttempts) setTimeout(tick, intervalMs);
                }
            }
        };

        const t = setTimeout(tick, 800);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [
        proactivePreview?.polling,
        proactivePreview?.domainLabel,
        proactivePreview?.futureGoalLabel,
        id,
        type,
        fetchProactiveList,
    ]);

    useEffect(() => {
        if (!remedialPreview?.polling || type !== "lesson") return;
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 45;
        const intervalMs = 3000;

        const tick = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const { data, error } = await previewReviewRemedialLessonApiV1AdminReviewLessonItemUuidRemedialPreviewGet({
                    path: { item_uuid: id },
                });
                const resData = (data || {}) as any;
                if (error) {
                    if (!cancelled) {
                        setRemedialPreview((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      polling: false,
                                      pollError:
                                          typeof (data as any)?.error === "string"
                                            ? (data as any).error
                                              : "Failed to load remedial preview",
                                  }
                                : null,
                        );
                    }
                    return;
                }

                const structured = data.structured_content ?? null;
                const serverReady = data.ready === true;
                const contentValid = isStructuredRemedialData(structured) || isProfessionalRemedialData(structured);
                const ready = serverReady && contentValid;
                const doneWithBadContent = serverReady && !contentValid;
                const status = typeof data.status === "string" ? data.status : null;

                if (!cancelled) {
                    setRemedialPreview((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  uuid: typeof data.uuid === "string" ? data.uuid : prev.uuid,
                                  structured,
                                  status,
                                  polling: !ready && !doneWithBadContent && attempts < maxAttempts,
                                  pollError: null,
                              }
                            : null,
                    );
                }

                if (ready || doneWithBadContent || attempts >= maxAttempts) {
                    if (!ready && !cancelled) {
                        setRemedialPreview((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      polling: false,
                                      pollError:
                                          status && /fail|error/i.test(status)
                                              ? `Generation status: ${status}`
                                              : doneWithBadContent
                                                ? "Generation saved invalid content. Delete the saved version and regenerate."
                                                : "Timed out waiting for remedial structured content. Check workers or try again.",
                                  }
                                : null,
                        );
                    }
                    return;
                }
                setTimeout(tick, intervalMs);
            } catch {
                if (!cancelled) {
                    if (attempts < maxAttempts) setTimeout(tick, intervalMs);
                }
            }
        };

        const t = setTimeout(tick, 800);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [remedialPreview?.polling, id, type]);

    const handleSelectProactive = useCallback((entry: ProactiveListEntry) => {
        setProactivePreview({
            domainLabel: entry.domain_label ?? "—",
            futureGoalLabel: entry.future_goal_label ?? "—",
            structured: entry.structured_content,
            status: entry.status,
            polling: false,
            pollError: null,
            selectedRowId: entry.id,
        });
    }, []);

    const fetchLatestRemedialPreview = useCallback(async () => {
        if (type !== "lesson") return;
        try {
            const { data, error } = await previewReviewRemedialLessonApiV1AdminReviewLessonItemUuidRemedialPreviewGet({
                path: { item_uuid: id },
            });
            const resData = (data || {}) as any;
            if (error) return;
            setRemedialPreview((prev) => ({
                uuid: typeof resData.uuid === "string" ? resData.uuid : prev?.uuid ?? null,
                structured: resData.structured_content ?? prev?.structured ?? null,
                status: typeof resData.status === "string" ? resData.status : prev?.status ?? null,
                polling: false,
                pollError: null,
            }));
        } catch {
            // no-op: keep current state
        }
    }, [id, type]);

    const handleSelectRemedial = useCallback(() => {
        setSidebarMode("remedial");
        if (!remedialPreview) {
            void fetchLatestRemedialPreview();
        }
    }, [remedialPreview, fetchLatestRemedialPreview]);

    useEffect(() => {
        if (sidebarMode !== "remedial") return;
        if (remedialPreview) return;
        void fetchLatestRemedialPreview();
    }, [sidebarMode, remedialPreview, fetchLatestRemedialPreview]);

    const handleSaveProactivePdf = useCallback(async () => {
        if (!proactivePdfContentRef.current) return;
        if (!proactivePreview || (!isProactiveStructuredData(proactivePreview.structured) && !isProfessionalProactiveStructuredData(proactivePreview.structured))) {
            showToast("Select a proactive lesson with structured content first", "error");
            return;
        }
        setIsSavingProactivePdf(true);
        try {
            const courseTitle = ((draftItem as any)?.lessons?.modules?.courses?.title as string) ?? "Course";
            const baseLessonTitle = ((draftItem as any)?.lessons?.title as string) ?? "Lesson";
            const variantSuffix = `${proactivePreview.domainLabel} - ${proactivePreview.futureGoalLabel}`;
            const filename = lessonPdfFilename(courseTitle, `${baseLessonTitle} (Proactive: ${variantSuffix})`);
            await exportLessonRootToPdf(proactivePdfContentRef.current, filename);
            showToast("Proactive preview saved as PDF");
        } catch (error) {
            console.error(error);
            showToast("Failed to save proactive preview as PDF", "error");
        } finally {
            setIsSavingProactivePdf(false);
        }
    }, [proactivePreview, draftItem]);

    const handleUpdateChange = (updatedData: Record<string, unknown> & { title?: string; generated_text?: string; structured_content?: unknown; description?: string; questions?: unknown[] }) => {
        setDraftItem((prev) => ({ ...prev, ...updatedData } as ReviewItem));
    };

    const handleSave = async (note: string = "Manual Edit") => {
        if (!draftItem) return;
        setIsSaving(true);
        try {
            const { error } = await updateReviewItemApiV1AdminReviewReviewTypeItemIdPatch({
                path: { review_type: type, item_id: id },
                body: {
                    ...draftItem,
                    change_note: note,
                } as any,
            });

            if (error) throw new Error("Failed to save changes");

            await fetchItem();
            setIsEditing(false);
            setHasUnsavedChanges(false);
            showToast("Changes saved successfully");
        } catch (err) {
            console.error(err);
            showToast("Failed to save changes", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRestore = async (updatedData: ReviewItem, note: string) => {
        setIsSaving(true);
        try {
            const { error } = await updateReviewItemApiV1AdminReviewReviewTypeItemIdPatch({
                path: { review_type: type, item_id: id },
                body: {
                    ...updatedData,
                    change_note: note,
                } as any,
            });

            if (error) throw new Error("Failed to restore version");

            await fetchItem();
            showToast("Version restored successfully");
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error(err);
            showToast("Failed to restore version", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleApprove = async () => {
        if (hasUnsavedChanges) {
            showToast("Please save your changes before approving.", "error");
            return;
        }

        if (!confirm("Are you sure you want to approve this content?")) return;

        setIsSaving(true);
        try {
            const { error } = await approveReviewItemApiV1AdminReviewReviewTypeItemIdApprovePost({
                path: { review_type: type, item_id: id },
            });

            if (error) {
                const errorText = (error as any)?.error || "Approve failed";
                throw new Error(`Failed to approve item: ${errorText}`);
            }

            showToast("Item Approved! Redirecting...");

            // Determine redirect URL
            let redirectUrl = "/admin/courses";
            if (type === "lesson") {
                const courseId = (draftItem as any)?.lessons?.modules?.courses?.id;
                if (courseId) redirectUrl = `/admin/courses/${courseId}`;
            } else if (type === "placement_quiz") {
                const courseId = (draftItem as any)?.course_id;
                if (courseId) redirectUrl = `/admin/courses/${courseId}`;
            } else if (type === "course_placement") {
                const courseId = (draftItem as any)?.id;
                if (courseId) redirectUrl = `/admin/courses/${courseId}`;
            }

            navigate(redirectUrl);
        } catch (err) {
            console.error(err);
            showToast("Failed to approve item", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleReject = () => {
        setIsRejectModalOpen(true);
    };

    const handleConfirmReject = async (reason: string) => {
        setIsSaving(true);
        try {
            const { error } = await updateReviewItemApiV1AdminReviewReviewTypeItemIdPatch({
                path: { review_type: type, item_id: id },
                body: {
                    status: "REJECTED",
                    rejectionComment: reason,
                } as any,
            });

            if (error) throw new Error("Failed to reject item");

            showToast("Item Rejected! Redirecting...");
            setIsRejectModalOpen(false);

            // Determine redirect URL
            let redirectUrl = "/admin/courses";
            if (type === "lesson") {
                const courseId = (draftItem as any)?.lessons?.modules?.courses?.id;
                if (courseId) redirectUrl = `/admin/courses/${courseId}`;
            } else if (type === "placement_quiz") {
                const courseId = (draftItem as any)?.course_id;
                if (courseId) redirectUrl = `/admin/courses/${courseId}`;
            } else if (type === "course_placement") {
                const courseId = (draftItem as any)?.id;
                if (courseId) redirectUrl = `/admin/courses/${courseId}`;
            }

            navigate(redirectUrl);
        } catch (err) {
            console.error(err);
            showToast("Failed to reject item", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const variants = (() => {
        if (!draftItem) return [];
        const currentVariant = {
            uuid: id,
            domain: (draftItem as any).domain,
            future_goal: (draftItem as any).future_goal,
            status: (draftItem as any).status,
            created_at: (draftItem as any).created_at
        };
        const siblings = (draftItem as any).siblings || [];
        return [currentVariant, ...siblings].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    })();

    const reviewActionsDisabled = isViewingVersion || isCanonicalRegenerating;
    const hasCanonicalStructuredContent =
        type === "lesson" ? Boolean((draftItem as any)?.structured_content) : true;

    const reviewMenuItems = (() => {
        const items: MenuItem[] = [];

        if (type === "lesson" && draftItem) {
            items.push({
                label: "Show quiz",
                icon: FileQuestion,
                onClick: () => navigate(`/admin/reviews/lesson/${id}/quiz`),
            });
            items.push({
                label: "Generate proactive",
                icon: Sparkles,
                onClick: () => setIsGenerateProactiveOpen(true),
                disabled: reviewActionsDisabled || !hasCanonicalStructuredContent,
            });
            items.push({
                label: "Generate remedial",
                icon: Sparkles,
                onClick: () => setIsGenerateRemedialOpen(true),
                disabled: reviewActionsDisabled || !hasLessonQuiz,
            });
            items.push({
                label: hasCanonicalStructuredContent ? "Regenerate" : "Generate canonical lesson",
                icon: RefreshCw,
                onClick: () => {
                    if (hasCanonicalStructuredContent) {
                        setIsRegenerateDialogOpen(true);
                        return;
                    }
                    const lessonId = (draftItem as any)?.lesson_id;
                    if (!lessonId) return;
                    setIsCanonicalRegenerating(true);
                    queueReviewCanonicalGenerationApiV1AdminReviewLessonItemUuidGenerateCanonicalPost({
                        path: { item_uuid: id },
                    })
                        .then(() => fetchItem())
                        .finally(() => setIsCanonicalRegenerating(false));
                },
                disabled: reviewActionsDisabled,
            });
        }

        items.push({
            label: isEditing ? "Cancel edit" : "Request changes",
            icon: PenSquare,
            onClick: () => setIsEditing(!isEditing),
            disabled: reviewActionsDisabled,
        });

        if (isEditing) {
            items.push({
                label: "Save",
                icon: Save,
                onClick: () => handleSave(),
                disabled: isSaving || isViewingVersion,
            });
        }

        items.push({
            label: "Reject",
            icon: XCircle,
            onClick: handleReject,
            disabled: reviewActionsDisabled,
            className: "text-destructive",
        });

        return items;
    })();

    const backHref = (() => {
        if (type === "lesson") {
            const courseId = (draftItem as any)?.lessons?.modules?.courses?.id;
            if (courseId) return `/admin/courses/${courseId}`;
        } else if (type === "placement_quiz") {
            const courseId = (draftItem as any)?.course_id;
            if (courseId) return `/admin/courses/${courseId}`;
        } else if (type === "course_placement") {
            const courseId = (draftItem as any)?.id;
            if (courseId) return `/admin/courses/${courseId}`;
        }
        return "/admin/courses";
    })();

    const lessonTypeBadge =
        type === "lesson" && draftItem
            ? (() => {
                  const raw = (draftItem as { lessons?: { lesson_type?: string } })?.lessons?.lesson_type;
                  if (typeof raw !== "string" || !raw.trim()) return null;
                  return raw
                      .trim()
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase());
              })()
            : null;

    return (
        <div className="h-[calc(100vh-theme(spacing.24))] flex flex-col relative">
            <div className="flex items-center justify-between mb-6 flex-shrink-0 sticky top-0 z-30 bg-card border-b border-border -mx-8 px-8 py-4 shadow-sm transition-all">
                <div className="flex items-center gap-4">
                    <Link
                        to={backHref}
                        className="p-2 rounded-full bg-muted text-muted-foreground hover:text-foreground hover:bg-secondary ring-1 ring-border transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 text-muted-foreground mb-0.5">
                            <span className="uppercase tracking-wide font-bold text-xs">{type}</span>
                            <span>•</span>
                            {lessonTypeBadge && (
                                <span
                                    className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-md bg-muted text-muted-foreground border border-border"
                                    title="Lesson type"
                                >
                                    {lessonTypeBadge}
                                </span>
                            )}
                        </div>
                        <h1 className="text-2xl font-bold text-foreground flex flex-wrap items-center gap-2">
                            <span>
                                {type === "lesson" && draftItem ? ((draftItem as any)?.lessons?.title || "Lesson Review") :
                                    type === "course_placement" ? "Placement Test Review" :
                                        type === "quiz" ? "Assessment Review" : "Review"}
                            </span>
                            
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {isCanonicalRegenerating && (
                        <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                            Canonical lesson is regenerating…
                        </span>
                    )}
                    <MenuDropdown
                        label="Actions"
                        icon={MoreHorizontal}
                        variant="outline"
                        align="right"
                        items={reviewMenuItems}
                    />
                    <button
                        onClick={handleApprove}
                        disabled={hasUnsavedChanges || isViewingVersion}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${hasUnsavedChanges || isViewingVersion
                            ? "bg-muted text-muted-foreground cursor-not-allowed"
                            : "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20"
                            }`}
                        title={
                            isViewingVersion
                                ? "Exit preview to approve"
                                : hasUnsavedChanges
                                    ? "Save changes before approving"
                                    : ""
                        }
                    >
                        <CheckCircle className="w-5 h-5" />
                        Approve
                    </button>
                    {hasUnsavedChanges && (
                        <div className="absolute top-full right-0 lg:top-auto lg:mt-0 mt-2 lg:mr-52 text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded border border-amber-200 animate-pulse">
                            Unsaved Changes
                        </div>
                    )}
                </div>
            </div>

            {
                variants.length > 1 && (
                    <div className="bg-card border-b border-border px-8 py-4 mb-4">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                            Generated Variants ({variants.length})
                        </h3>
                        <div className="flex flex-wrap gap-3">
                            {variants.map((v: any) => (
                                <button
                                    key={v.uuid}
                                    onClick={() => navigate(`/admin/reviews/${type}/${v.uuid}`)}
                                    className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left transition-all ${v.uuid === id
                                        ? "bg-primary/10 border-primary/20 ring-1 ring-primary/20"
                                        : "bg-background border-border hover:border-primary/50 hover:bg-muted/50"
                                        }`}
                                >
                                    <div className={`text-sm font-bold flex items-center gap-1.5 ${v.uuid === id ? "text-primary" : "text-foreground"}`}>
                                        {v.domain || "Default"}
                                        {v.future_goal && (
                                            <span className="opacity-70 font-normal text-xs">• {v.future_goal}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${v.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" :
                                            v.status === "REJECTED" ? "bg-red-100 text-red-700" :
                                                "bg-amber-100 text-amber-700"
                                            }`}>
                                            {v.status || "PENDING"}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground">
                                            {new Date(v.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )
            }

            <div className="pt-2">
                {loading ? (
                    <div>Loading...</div>
                ) : error ? (
                    <div className="text-red-500">{error}</div>
                ) : draftItem && (
                    type === "lesson" ? (
                        <>
                            <LessonReviewContent
                                item={draftItem as never}
                                isEditing={isEditing}
                                onUpdate={handleUpdateChange}
                                onRestore={handleRestore}
                                onUnsavedChange={setHasUnsavedChanges}
                                onViewingVersionChange={setIsViewingVersion}
                                sidebarMode={sidebarMode}
                                onSidebarModeChange={setSidebarMode}
                                proactiveList={proactiveList}
                                proactiveListLoading={proactiveListLoading}
                                onSelectProactive={handleSelectProactive}
                                selectedProactiveId={proactivePreview?.selectedRowId ?? null}
                                hasRemedialPreview={Boolean(remedialPreview)}
                                remedialPolling={Boolean(remedialPreview?.polling)}
                                onSelectRemedial={handleSelectRemedial}
                                proactivePanel={
                                    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border bg-muted/40">
                                            <div className="min-w-0">
                                                <h2 className="text-sm font-bold text-foreground">
                                                    Proactive lesson preview (student view)
                                                </h2>
                                                {proactivePreview && (
                                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                                        {proactivePreview.domainLabel} · {proactivePreview.futureGoalLabel}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                {proactivePreview?.polling && (
                                                    <span className="text-xs font-medium text-primary animate-pulse">
                                                        Generating…
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => void handleSaveProactivePdf()}
                                                    disabled={
                                                        isSavingProactivePdf ||
                                                        !proactivePreview ||
                                                        (!isProactiveStructuredData(proactivePreview.structured) && !isProfessionalProactiveStructuredData(proactivePreview.structured))
                                                    }
                                                    className="text-xs font-medium px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isSavingProactivePdf ? "Saving PDF..." : "Save to PDF"}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="p-6 w-full">
                                            {!proactivePreview && (
                                                <p className="text-sm text-muted-foreground">
                                                    Pick a domain · goal variant in the list, or use Actions → Generate
                                                    proactive.
                                                </p>
                                            )}
                                            {proactivePreview?.pollError && (
                                                <p className="text-sm text-red-600 mb-4">{proactivePreview.pollError}</p>
                                            )}
                                            {proactivePreview &&
                                                isProfessionalProactiveStructuredData(proactivePreview.structured) && (
                                                    <div className="rounded-lg border border-border bg-background p-4">
                                                        <div ref={proactivePdfContentRef} className="mx-auto max-w-4xl">
                                                            <ProfessionalProactiveLessonContent data={proactivePreview.structured} />
                                                        </div>
                                                    </div>
                                                )}
                                            {proactivePreview &&
                                                !isProfessionalProactiveStructuredData(proactivePreview.structured) &&
                                                isProactiveStructuredData(proactivePreview.structured) && (
                                                    <div className="rounded-lg border border-border bg-background p-4">
                                                        <div ref={proactivePdfContentRef} className="mx-auto max-w-4xl">
                                                            <ProactiveLessonContent data={proactivePreview.structured} />
                                                        </div>
                                                    </div>
                                                )}
                                            {proactivePreview &&
                                                !proactivePreview.polling &&
                                                !proactivePreview.pollError &&
                                                !isProactiveStructuredData(proactivePreview.structured) &&
                                                !isProfessionalProactiveStructuredData(proactivePreview.structured) && (
                                                    <p className="text-sm text-muted-foreground">
                                                        No structured proactive JSON for this row yet.
                                                    </p>
                                                )}
                                        </div>
                                    </div>
                                }
                                remedialPanel={
                                    <div className="rounded-xl border border-amber-200 bg-card shadow-sm overflow-hidden">
                                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-amber-100 bg-amber-50/60">
                                            <div className="min-w-0">
                                                <h2 className="text-sm font-bold text-foreground">
                                                    Remedial lesson preview (student view)
                                                </h2>
                                            </div>
                                            {remedialPreview?.polling && (
                                                <span className="text-xs font-medium text-amber-700 animate-pulse">
                                                    Generating…
                                                </span>
                                            )}
                                        </div>
                                        <div className="p-6 w-full">
                                            {remedialPreview?.pollError && (
                                                <p className="text-sm text-red-600 mb-4">{remedialPreview.pollError}</p>
                                            )}
                                            {(isStructuredRemedialData(remedialPreview?.structured) || isProfessionalRemedialData(remedialPreview?.structured)) ? (
                                                <div className="rounded-lg border border-border bg-background p-4">
                                                    <RemedialLessonContent data={remedialPreview.structured as RemedialLessonData | ProfessionalRemedialLessonData} />
                                                </div>
                                            ) : (
                                                !remedialPreview?.polling &&
                                                !remedialPreview?.pollError && (
                                                    <p className="text-sm text-muted-foreground">
                                                        No structured remedial content yet. Use Actions → Generate
                                                        remedial.
                                                    </p>
                                                )
                                            )}
                                        </div>
                                    </div>
                                }
                            />
                        </>
                    ) : (
                        <QuizReviewContent
                            item={draftItem as never}
                            isEditing={isEditing}
                            onUpdate={handleUpdateChange}
                        />
                    )
                )}
            </div>

            {
                toast && (
                    <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-md shadow-lg text-white font-medium z-50 transition-opacity animate-fade-in-up ${toast.type === "success" ? "bg-emerald-600" : "bg-red-600"}`}>
                        {toast.message}
                    </div>
                )
            }
            <RejectionModal
                isOpen={isRejectModalOpen}
                onClose={() => setIsRejectModalOpen(false)}
                onConfirm={handleConfirmReject}
                isSubmitting={isSaving}
            />
            {type === "lesson" && draftItem && (
                <>
                    <RegenerateLessonDialog
                        isOpen={isRegenerateDialogOpen}
                        onClose={() => setIsRegenerateDialogOpen(false)}
                        generatedText={(draftItem as any).generated_text ?? ""}
                        itemUuid={id}
                        canonicalLessonId={(draftItem as any).lesson_id ?? null}
                        lessonTitle={(draftItem as any).lessons?.title ?? ""}
                        initialKeyConcepts={
                            Array.isArray((draftItem as any).lessons?.key_concepts)
                                ? (draftItem as any).lessons.key_concepts
                                : []
                        }
                        onSuccess={fetchItem}
                        onToast={showToast}
                        onRegenerationStarted={() => setIsCanonicalRegenerating(true)}
                    />
                    {(() => {
                        const courseId = (draftItem as any)?.lessons?.modules?.courses?.id as number | undefined;
                        if (!courseId) return null;
                        return (
                            <GenerateProactiveDialog
                                isOpen={isGenerateProactiveOpen}
                                onClose={() => setIsGenerateProactiveOpen(false)}
                                courseId={courseId}
                                itemUuid={id}
                                onToast={showToast}
                                onQueued={(sel) => {
                                    setProactivePreview({
                                        domainLabel: sel.domainLabel,
                                        futureGoalLabel: sel.futureGoalLabel,
                                        structured: null,
                                        status: null,
                                        polling: true,
                                        pollError: null,
                                        selectedRowId: undefined,
                                    });
                                    void fetchProactiveList();
                                }}
                            />
                        );
                    })()}
                    <GenerateRemedialDialog
                        isOpen={isGenerateRemedialOpen}
                        onClose={() => setIsGenerateRemedialOpen(false)}
                        itemUuid={id}
                        onToast={showToast}
                        onQueued={() => {
                            setSidebarMode("remedial");
                            setRemedialPreview({
                                uuid: null,
                                structured: null,
                                status: null,
                                polling: true,
                                pollError: null,
                            });
                        }}
                    />
                </>
            )}
        </div >
    );
}
